import { type TypedDocumentNode, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { notifyUnlessSurfaced } from 'src/lib/apollo/appErrorLink';
import { notify } from 'src/lib/notify';
import {
  initialPagedGridInput,
  PAGED_LIST_FETCH_POLICY,
  type PagedGridQueryState,
  usePreloadedServerGridQuery,
} from 'src/modules/grids';
import { preloadRouteQuery } from 'src/routing/routePreload';
import type { AppRouterContext } from 'src/routing/routerAccess';
import type {
  CreateOrgPlace,
  CreateOrgPlaceVariables,
  CreatePlaceInput,
  DeleteOrgPlace,
  DeleteOrgPlaceVariables,
  OrganizationPlacePageQuery,
  OrganizationPlacePageQueryVariables,
  UpdateOrgPlace,
  UpdateOrgPlaceVariables,
  UpdatePlaceInput,
} from 'types/graphql';
import { useStableRows } from '../useStableRows';

/** Where the My Addresses grid persists its layout. v1 held LOCAL sort/filter/search state. */
export const MY_ADDRESSES_GRID_STORAGE_KEY = 'my-addresses_v2';

/**
 * The My Addresses grid's bounded read (TAS-3660 P7 BZ76): the organization's own places
 * (`businessEntityId: null`), one server page per request, same node selection as before.
 */
const ORGANIZATION_PLACE_PAGE_QUERY: TypedDocumentNode<
  OrganizationPlacePageQuery,
  OrganizationPlacePageQueryVariables
> = gql`
  query OrganizationPlacePageQuery($input: StandardPageInput!) {
    placePage(input: $input, businessEntityId: null) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          description
          streetAddress1
          streetAddress2
          city
          state
          postalCode
          addressCountry
          phone
          googlePlaceId
          addressRoles
          disabled
          isActive
          primaryContact {
            id
            name
          }
          warehouse {
            id
          }
        }
      }
    }
  }
`;

const CREATE_ORG_PLACE = gql`
  mutation CreateOrgPlace($input: CreatePlaceInput!) {
    createPlace(input: $input) {
      id
      description
      streetAddress1
      streetAddress2
      city
      state
      postalCode
      addressCountry
      phone
      googlePlaceId
      addressRoles
      disabled
      isActive
      primaryContact {
        id
        name
      }
    }
  }
`;

const UPDATE_ORG_PLACE = gql`
  mutation UpdateOrgPlace($id: Int!, $input: UpdatePlaceInput!) {
    updatePlace(id: $id, input: $input) {
      id
      description
      streetAddress1
      streetAddress2
      city
      state
      postalCode
      addressCountry
      phone
      googlePlaceId
      addressRoles
      disabled
      isActive
      primaryContact {
        id
        name
      }
    }
  }
`;

const DELETE_ORG_PLACE = gql`
  mutation DeleteOrgPlace($id: Int!) {
    deletePlace(id: $id) {
      id
    }
  }
`;

export type OrgPlace = {
  id: number;
  description?: string | null;
  streetAddress1?: string | null;
  streetAddress2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  addressCountry?: string | null;
  phone?: string | null;
  googlePlaceId?: string | null;
  addressRoles?: string[] | null;
  disabled?: boolean;
  isActive?: boolean;
  primaryContact?: { id: number; name: string } | null;
  warehouse?: { id: number } | null;
};

export function useOrgPlaces(gridState: PagedGridQueryState) {
  const page = usePreloadedServerGridQuery({
    queryKey: 'placePage',
    query: ORGANIZATION_PLACE_PAGE_QUERY,
    gridState,
    label: 'addresses',
  });
  const places = useStableRows(page.rows as OrgPlace[]);
  return { ...page, places };
}

/** The grid's first page as it will ask for it; the My Addresses tab preloads it on intent. */
export function preloadMyAddressesGrid(context: AppRouterContext) {
  const input = initialPagedGridInput(MY_ADDRESSES_GRID_STORAGE_KEY);
  return preloadRouteQuery(context, ORGANIZATION_PLACE_PAGE_QUERY, { input }, { fetchPolicy: PAGED_LIST_FETCH_POLICY });
}

export function useCreateOrgPlace() {
  const [createPlace, { loading }] = useMutation<CreateOrgPlace, CreateOrgPlaceVariables>(CREATE_ORG_PLACE, {
    refetchQueries: [ORGANIZATION_PLACE_PAGE_QUERY],
    onCompleted: () => {
      notify.success('Address created successfully');
    },
    onError: (error) => notifyUnlessSurfaced(error, 'Failed to create address.'),
  });

  return {
    createPlace: useCallback(
      async (input: Omit<CreatePlaceInput, 'businessEntityId'>) => {
        const { data } = await createPlace({ variables: { input } });
        return data?.createPlace;
      },
      [createPlace]
    ),
    loading,
  };
}

export function useUpdateOrgPlace() {
  const [updatePlace, { loading }] = useMutation<UpdateOrgPlace, UpdateOrgPlaceVariables>(UPDATE_ORG_PLACE, {
    refetchQueries: [ORGANIZATION_PLACE_PAGE_QUERY],
    onCompleted: () => {
      notify.success('Address updated successfully');
    },
    onError: (error) => notifyUnlessSurfaced(error, 'Failed to update address.'),
  });

  return {
    updatePlace: useCallback(
      async (id: number, input: UpdatePlaceInput) => {
        const { data } = await updatePlace({ variables: { id, input } });
        return data?.updatePlace;
      },
      [updatePlace]
    ),
    loading,
  };
}

export function useDeleteOrgPlace() {
  const [deletePlace, { loading }] = useMutation<DeleteOrgPlace, DeleteOrgPlaceVariables>(DELETE_ORG_PLACE, {
    refetchQueries: [ORGANIZATION_PLACE_PAGE_QUERY],
    onCompleted: () => {
      notify.success('Address deleted successfully');
    },
    onError: (error) => notifyUnlessSurfaced(error, 'Failed to delete address.'),
  });

  return {
    deletePlace: useCallback(
      async (id: number) => {
        const { data } = await deletePlace({ variables: { id } });
        return data?.deletePlace;
      },
      [deletePlace]
    ),
    loading,
  };
}
